// schema.org JSON-LD builders for Google rich results.
// Used by SEO-sensitive pages (course detail, etc.).
// Note: site-wide Organization + LocalBusiness schema is defined in
// public/index.html and applies to every page automatically.

const SITE_URL = 'https://e-matrix.at';
const ORG_NAME = 'e-Matrix Innovations';

// BCP-47 language tags for schema.org inLanguage
const SCHEMA_LANGUAGE_TAGS = {
  at: 'de-AT',
  de: 'de-DE',
  en: 'en',
  cz: 'cs',
  hu: 'hu',
  ro: 'ro',
  pl: 'pl',
  fr: 'fr',
  it: 'it',
  zh: 'zh',
  es: 'es',
  sk: 'sk',
  hr: 'hr',
  sl: 'sl',
  sr: 'sr',
  bg: 'bg',
  tr: 'tr',
  ru: 'ru'
};

/**
 * Best-effort parse of free-text duration strings like
 * "45 Minuten", "~30 min", "1 Stunde", "2h" into ISO 8601 duration (PT...).
 * Returns null if no integer minute count can be extracted.
 */
export function parseDurationToISO8601(text) {
  if (!text || typeof text !== 'string') return null;
  // Look for hour + minute combos
  const hourMatch = text.match(/(\d+)\s*(?:h|std|stunde)/i);
  const minuteMatch = text.match(/(\d+)\s*(?:m|min|minute)/i);
  let mins = 0;
  if (hourMatch) mins += parseInt(hourMatch[1], 10) * 60;
  if (minuteMatch) mins += parseInt(minuteMatch[1], 10);
  if (mins === 0) {
    // fallback: first number assumed to be minutes
    const fallback = text.match(/(\d+)/);
    if (!fallback) return null;
    mins = parseInt(fallback[1], 10);
  }
  if (!mins || isNaN(mins)) return null;
  return `PT${mins}M`;
}

/**
 * Build a schema.org/Course object for Google rich results.
 * See https://developers.google.com/search/docs/appearance/structured-data/course-info
 */
export function buildCourseJsonLd({ course, language, path, ogImage = null }) {
  if (!course) return null;

  const name = course.title?.[language] || course.title?.de || '';
  const aboutText =
    typeof course.aboutCourse === 'object'
      ? course.aboutCourse?.[language]
      : language === 'de'
      ? course.aboutCourse
      : null;
  const description =
    aboutText || course.description?.[language] || course.description?.de || name;

  const inLanguage = (course.languages || [])
    .map((l) => SCHEMA_LANGUAGE_TAGS[l] || l)
    .filter(Boolean);

  const teaches = course.learningObjectives?.[language] || course.learningObjectives?.de;
  const iso8601Duration = parseDurationToISO8601(course.duration);

  const courseInstance = {
    '@type': 'CourseInstance',
    courseMode: 'online'
  };
  if (iso8601Duration) {
    courseInstance.courseWorkload = iso8601Duration;
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name,
    description,
    url: `${SITE_URL}/${language}${path}`,
    provider: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: ORG_NAME,
      sameAs: SITE_URL
    },
    hasCourseInstance: courseInstance
  };

  if (ogImage) {
    // Prefer the branded 1200×630 social card (shared by og:image + schema)
    jsonLd.image = ogImage;
  } else if (course.image) {
    jsonLd.image = course.image.startsWith('http') ? course.image : `${SITE_URL}${course.image}`;
  }
  if (inLanguage.length > 0) {
    jsonLd.inLanguage = inLanguage.length === 1 ? inLanguage[0] : inLanguage;
  }
  if (teaches) {
    jsonLd.teaches = teaches;
  }
  if (course.id) {
    jsonLd.courseCode = course.id;
  }

  return jsonLd;
}
